import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box,
    Button,
    Flex,
    IconButton,
    Image,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    CellContext,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import { useMemo, useRef, useState } from 'react';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import { SponsorDto } from '../../../models/sponsor/dto/sponsor.dto';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useDeleteSponsor } from '../hooks/useDeleteSponsor';
import { useListSponsors } from '../hooks/useListSponsors';
import CreateSponsorForm from './CreateSponsorForm';
import ErrorState from '../../../components/states/ErrorState';
import { tableProps, tableThProps, tableThFlexProps, tableCellProps, tableHeaderTextProps } from 'theme/constants';

const columnHelper = createColumnHelper<SponsorDto>();

export default function SponsorList() {
    const [includeInactive, setIncludeInactive] = useState(false);
    const { selectedOrganization } = useSelectedOrganization();
    // `useListSponsors` is `enabled: !!organizationId` and this component has no
    // scope guard, so the raw `isLoading` it used to read was permanently true
    // for an operator with no organization selected — and because that arm was
    // an EARLY RETURN above `PageCard`, what they saw was a bare skeleton table
    // with no page title and no "Create Sponsor" button: a page that looks like
    // it is still arriving and never does. `classifyRead` takes the query's own
    // facts rather than a restatement of its gate (D62), and the never-issued
    // case now falls THROUGH to the page chrome so the prompt sits where the
    // table would be.
    const { data, isInitialLoading, isError } = useListSponsors(selectedOrganization?.id || '', includeInactive);
    const readState = classifyRead(isError, isInitialLoading, data);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const toast = useToast();

    // Edit modal
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const [editingSponsor, setEditingSponsor] = useState<SponsorDto | null>(null);

    // Delete modal
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingSponsor, setDeletingSponsor] = useState<SponsorDto | null>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    // Mutations
    const { mutateAsync: deleteSponsor, isLoading: isDeleting } = useDeleteSponsor();

    const handleEditSponsor = (sponsor: SponsorDto) => {
        setEditingSponsor(sponsor);
        onEditOpen();
    };

    const handleCloseEdit = () => {
        setEditingSponsor(null);
        onEditClose();
    };

    const handleDeleteSponsor = (sponsor: SponsorDto) => {
        setDeletingSponsor(sponsor);
        onDeleteOpen();
    };

    const handleConfirmDelete = async () => {
        if (!deletingSponsor) return;

        try {
            await deleteSponsor(deletingSponsor.id);
            toast({
                title: translate('sponsor.delete_success') || 'Sponsor deleted successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onDeleteClose();
            setDeletingSponsor(null);
        } catch (error: any) {
            toast({
                title: translate('sponsor.delete_error') || 'Error deleting sponsor',
                description: error.message || translate('sponsor.delete_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('sponsor.name') || 'Name'}
                </Text>
            ),
            cell: (info: CellContext<SponsorDto, string>) => (
                <Flex align='center' gap={3}>
                    {info.row.original.logoUrl && (
                        <Image
                            src={info.row.original.logoUrl}
                            alt={info.getValue()}
                            boxSize="40px"
                            objectFit="contain"
                            borderRadius="md"
                        />
                    )}
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('websiteUrl', {
            id: 'website',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('sponsor.website') || 'Website'}
                </Text>
            ),
            cell: (info: CellContext<SponsorDto, string | undefined>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue() || '-'}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('isActive', {
            id: 'isActive',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('sponsor.active') || 'Active'}
                </Text>
            ),
            cell: (info: CellContext<SponsorDto, boolean>) => (
                <Flex align='center'>
                    <Badge colorScheme={info.getValue() ? 'green' : 'gray'}>
                        {info.getValue() ? translate('common.yes') || 'Yes' : translate('common.no') || 'No'}
                    </Badge>
                </Flex>
            )
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('sponsor.created_at') || 'Created At'}
                </Text>
            ),
            cell: (info: CellContext<SponsorDto, string>) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {new Date(info.getValue()).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('common.actions') || 'Actions'}
                </Text>
            ),
            cell: (info: CellContext<SponsorDto, unknown>) => (
                <Flex align='center' gap={2}>
                    <IconButton
                        aria-label="Edit sponsor"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditSponsor(info.row.original)}
                    />
                    <IconButton
                        aria-label="Delete sponsor"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDeleteSponsor(info.row.original)}
                    />
                </Flex>
            )
        }),
    ], [textColor]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: {
            sorting
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (readState === 'loading') {
        return <Loader isLoading />;
    }

    return (
        <PageCard
            title={translate('sponsor.sponsors') || 'Sponsors'}
            rightHeader={
                <Flex gap={2} align="center">
                    <Switch
                        isChecked={includeInactive}
                        onChange={(e) => setIncludeInactive(e.target.checked)}
                    />
                    <Text fontSize='sm'>{translate('sponsor.show_inactive') || 'Show Inactive'}</Text>
                    <Button
                        colorScheme="brand"
                        onClick={onEditOpen}
                        size="sm"
                    >
                        {translate('sponsor.create') || 'Create Sponsor'}
                    </Button>
                </Flex>
            }
        >
            {readState === 'never-issued' ? (
                <ScopePrompt gate='organization' />
            ) : readState === 'unreadable' ? (
                // Keyed on the classifier rather than on the raw `isError` it
                // replaces (R96-3): this list has `staleTime: 5 * 60 * 1000`, so
                // a background refetch that fails while rows are on screen used
                // to swap a good table for "Failed to load sponsors."
                <ErrorState message={translate('sponsor.error_loading') || 'Failed to load sponsors.'} />
            ) : table.getRowModel().rows.length === 0 ? (
                <Text px='25px' py='40px' textAlign='center' color='gray.500'>
                    {translate('sponsor.empty') || 'No sponsors found.'}
                </Text>
            ) : (
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <Th
                                        {...tableThProps}
                                        key={header.id}
                                        borderColor={borderColor}
                                        cursor='pointer'
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <Flex
                                            {...tableThFlexProps}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <SortIndicator direction={header.column.getIsSorted()} />
                                        </Flex>
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map(row => (
                            <Tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <Td
                                        key={cell.id}
                                        {...tableCellProps}
                                        borderColor={borderColor}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            )}

            <CreateSponsorForm
                sponsor={editingSponsor || undefined}
                isOpen={isEditOpen}
                onClose={handleCloseEdit}
            />

            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelButtonRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {translate('sponsor.delete_confirm') || 'Delete Sponsor'}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('sponsor.delete_confirm_message') || 'Are you sure? You can\'t undo this action afterwards.'}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                                {translate('common.cancel') || 'Cancel'}
                            </Button>
                            <Button colorScheme='red' onClick={handleConfirmDelete} ml={3} isLoading={isDeleting}>
                                {translate('common.delete') || 'Delete'}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </PageCard>
    );
}
